import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Printer } from "lucide-react";

interface Props { ownerId: string; }
interface Row {
  booking_id: string;
  start_time: string;
  end_time: string;
  address: string;
  driver: string;
  lot_count: number;
}

const PrintListTab = ({ ownerId }: Props) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    const load = async () => {
      const { data: spots } = await supabase
        .from("parking_spots")
        .select("id, address")
        .eq("owner_id", ownerId);
      const ids = (spots ?? []).map((s) => s.id);
      if (!ids.length) return setLoading(false);
      const addrMap = new Map((spots ?? []).map((s) => [s.id, s.address]));
      const now = new Date();
      const sod = new Date(now); sod.setHours(0, 0, 0, 0);
      const eod = new Date(now); eod.setHours(23, 59, 59, 999);

      const { data: bk } = await supabase
        .from("bookings")
        .select("id, user_id, parking_spot_id, start_time, end_time, lot_count, status")
        .in("parking_spot_id", ids)
        .gte("start_time", sod.toISOString())
        .lte("start_time", eod.toISOString())
        .neq("status", "cancelled")
        .order("start_time", { ascending: true });

      const userIds = Array.from(new Set((bk ?? []).map((b) => b.user_id).filter(Boolean) as string[]));
      const { data: profs } = userIds.length
        ? await supabase.from("profiles").select("id, first_name, last_name").in("id", userIds)
        : { data: [] as any[] };
      const profMap = new Map((profs ?? []).map((p: any) => [p.id, `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || "Guest"]));

      setRows(
        (bk ?? []).map((b) => ({
          booking_id: b.id,
          start_time: b.start_time,
          end_time: b.end_time,
          address: addrMap.get(b.parking_spot_id) ?? "—",
          driver: b.user_id ? profMap.get(b.user_id) ?? "Guest" : "Guest",
          lot_count: b.lot_count,
        }))
      );
      setLoading(false);
    };
    load();
  }, [ownerId]);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Today's Arrivals</CardTitle>
        <Button size="sm" variant="outline" onClick={() => window.print()}>
          <Printer className="h-4 w-4 mr-2" /> Print
        </Button>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No bookings today.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2">Time</th>
                  <th>Driver</th>
                  <th>Spot</th>
                  <th>Lots</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.booking_id} className="border-b">
                    <td className="py-2">
                      {new Date(r.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}–
                      {new Date(r.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td>{r.driver}</td>
                    <td className="max-w-xs truncate">{r.address}</td>
                    <td>{r.lot_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PrintListTab;
